<template>
  <div class="page">
    <!-- HERO -->
    <section
      class="hero-section"
      :style="{ backgroundImage: `url(${heroBg})` }"
    >
      <div class="hero">
        <img
          src="../assets/logoemail.png"
          alt="VentureStar EmPerA"
          class="hero-logo"
        />

        <h1 class="hero-title">
          VentureStar <span>EmPerA</span>
        </h1>

        <!-- VALUE BOX -->
        <div class="hero-value-box">
          <div class="hero-value-item">
            <strong>Private mailbox ingestion</strong>
            <p>
              Securely download and unify multiple mailboxes into a single,
              private email knowledge store through integration with Gmail and Outlook 365
            </p>
          </div>

          <div class="hero-value-item">
            <strong>AI-driven visual discovery</strong>
            <p>
              Explore, analyze, and understand large volumes of email data using
              intelligent visual tooling
            </p>
          </div>

          <div class="hero-value-item">
            <strong>Privacy Policy</strong>
            <p>
              We take user privacy very seriously <br />
              <a href="https://empera.venturestar.com/pp">
                Have a look at our Privacy Policy
              </a>
            </p>
          </div>
        </div>

        <!-- CTA -->
        <div class="cta-container">
          <p class="cta-text">Get started with EmPerA today</p>
          <a
            href="https://get.microsoft.com/installer/download/9pgnswhdwml6?referrer=appbadge"
            target="_self"
          >
            <img
              src="https://get.microsoft.com/images/en-us%20dark.svg"
              alt="Download from Microsoft"
              class="cta-badge"
            />
          </a>
        </div>
      </div>
    </section>

    <!-- SECTION 2 -->
    <section class="section dark">
      <div class="content-box">
        <div class="tagline">
          Turn overwhelming email archives into<br />
          <span>structured, searchable knowledge bases</span>
        </div>

        <div class="image-grid">
          <img class="image" :src="img1" @click="openImage(img1)" />
          <img class="image" :src="img2" @click="openImage(img2)" />
          <img class="image" :src="img3" @click="openImage(img3)" />
          <img class="image" :src="img4" @click="openImage(img4)" />
          <img class="image" :src="img5" @click="openImage(img5)" />
        </div>
      </div>
    </section>

    <!-- LIGHTBOX -->
    <div v-if="activeImage" class="lightbox" @click="closeImage">
      <img :src="activeImage" class="lightbox-img" />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from "vue";

import heroBg from "../assets/emailbgi.png";
import img1 from "../assets/tags2.png";
import img2 from "../assets/AItagging.png";
import img3 from "../assets/kb.png";
import img4 from "../assets/splash.png";
import img5 from "../assets/sync2.png";

const activeImage = ref(null);

function openImage(img) {
  activeImage.value = img;
}

function closeImage() {
  activeImage.value = null;
}

function handleKey(e) {
  if (e.key === "Escape") closeImage();
}

onMounted(() => {
  window.addEventListener("keydown", handleKey);
});

onBeforeUnmount(() => {
  window.removeEventListener("keydown", handleKey);
});
</script>

<style scoped lang="scss">
.page {
  width: 100%;
  overflow-x: hidden;
}

/* HERO */

.hero-section {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4rem 1.5rem;

  background-size: cover;
  background-position: center;
}

/* HERO CONTENT */

.hero {
  text-align: center;
  color: white;
  max-width: 1000px;
}

/* LOGO (reduced gap) */

.hero-logo {
  height: 160px;
  margin-bottom: 1rem; /* 🔧 reduced from 2rem */

  @media (max-width: 600px) {
    height: 100px;
  }
}

/* TITLE */

.hero-title {
  font-size: 5rem;
  font-weight: 800;
  margin-bottom: 2.5rem;
  line-height: 1.15;
  letter-spacing: 0.05em;

  span {
    color: #fff1c9;
  }

  @media (max-width: 600px) {
    font-size: 3rem;
    line-height: 1.2;
  }
}

/* VALUE BOX */

.hero-value-box {
  padding: 2.8rem;
  background: rgba(0, 0, 0, 0.55);
  border-radius: 18px;
  margin: 0 auto;
  max-width: 850px;
}

.hero-value-item {
  margin-bottom: 2rem;

  &:last-child {
    margin-bottom: 0;
  }

  strong {
    color: #ec2aaf;
    font-size: 1.5rem;
    display: block;
    margin-bottom: 0.4rem;
  }

  p {
    font-size: 1.2rem;
    line-height: 1.7;
    margin: 0;
  }
}

/* CTA */

.cta-container {
  margin-top: 3rem;
}

.cta-text {
  font-size: 1.25rem;
  margin-bottom: 0.6rem;
}

/* ✅ HOVER RESTORED */

.cta-badge {
  width: 230px;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.06);
  }

  @media (max-width: 600px) {
    width: 170px;
  }
}

/* SECTION */

.section {
  min-height: 100vh;
  padding: 5rem 1.5rem;
  display: flex;
  justify-content: center;
  align-items: center;
}

.dark {
  background: #111;
}

.content-box {
  max-width: 1100px;
  width: 100%;
  text-align: center;
}

.tagline {
  color: white;
  font-size: 2.4rem;
  margin-bottom: 3rem;
  line-height: 1.5;
  text-align: center;

  span {
    color: #ec2aaf;
  }

  @media (max-width: 600px) {
    font-size: 1.5rem;
  }
}

/* IMAGES */

.image-grid {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 16px;
}

.image {
  width: 340px;
  height: auto;
  object-fit: contain;
  border-radius: 8px;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.04);
  }

  @media (max-width: 600px) {
    width: 95%;
  }
}

/* LIGHTBOX */

.lightbox {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
  padding: 20px;
}

.lightbox-img {
  max-width: 95%;
  max-height: 95%;
  object-fit: contain;
  border-radius: 10px;
}
</style>